import { createSource } from '@platform/client';
import { defined } from '@platform/schema';
import { sourceFrom } from '@platform/schema/authoring';
import { sceneAsserts } from '@platform/schema/content';

/**
 * Widened on purpose. The scene arrives from the generated SDK, where `kind` is a string —
 * so narrowing it here would mean a cast at every call site to satisfy a lookup that is
 * already total over the real kinds.
 */
const ASSERTS: Record<string, boolean> = sceneAsserts;

/**
 * Whether this scene still owes the publish gate a source — spec 013 §17.
 *
 * `sceneIssues` refuses a CASE or ARTICLE without one, and nothing could set one after
 * creation, so a generated module said "must name its source" with no screen able to
 * answer it. Deliberately a person's job: `content-grounding` says the planner does not
 * invent evidence, and clearing this gate automatically is what that rule prevents.
 */
export function needsSource(
  kind: string,
  sourceId: string | null | undefined,
): boolean {
  return ASSERTS[kind] === true && !sourceId;
}

/** A typed line recorded as a shared source. Undefined when nothing in it is citable. */
export async function createSourceFrom(
  input: string,
  title: string,
): Promise<string | undefined> {
  const draft = sourceFrom(input, title);
  if (draft === undefined) return undefined;

  const created = await createSource({
    body: defined(draft),
    throwOnError: true,
  });
  return created.data.id;
}
